import { useCallback, useRef, useState } from 'react'
import { ref, uploadBytesResumable, getDownloadURL } from 'firebase/storage'

import { storage } from '../FirebaseInit'
import { toAttachmentRef } from '../utils/tickets/attachments'

/**
 * useAttachmentUpload
 *
 * Wraps Firebase Storage uploads for ticket attachments. Each file is uploaded
 * to `ticket-attachments/{customerId}/{ticketId || draftId}/{uuid}-{fileName}`
 * and, on success, shaped into an attachment reference via `toAttachmentRef`.
 *
 * Per-file failures are captured as `{ fileName, error }` entries in the
 * `error` state (rather than aborting the whole batch) so the caller can submit
 * the successfully uploaded subset and retry the failures individually. The
 * `retry(fileName)` action re-runs the upload for a previously-failed file
 * using the same target-path context it was originally given (Req 2.4).
 *
 * @param {Object} params
 * @param {string} params.customerId Owning customer id, used in the storage path.
 * @returns {{
 *   upload: (files: File[], context: Object) => Promise<Array>,
 *   uploading: boolean,
 *   progress: Object,
 *   error: Array,
 *   retry: (fileName: string) => Promise<(Object|null)>
 * }}
 */
const useAttachmentUpload = ({ customerId }) => {
  const [uploading, setUploading] = useState(false)
  const [progress, setProgress] = useState({})
  const [error, setError] = useState([])

  // Retain the File objects and their target-path context so a failed upload
  // can be retried without the caller re-supplying them.
  const pendingRef = useRef({})

  const buildStoragePath = useCallback((file, { ticketId, draftId }) => {
    const scope = ticketId || draftId
    const uuid = crypto.randomUUID()
    return `ticket-attachments/${customerId}/${scope}/${uuid}-${file.name}`
  }, [customerId])

  // Upload a single file, resolving to an attachment ref on success or null on
  // failure (recording a per-file `{ fileName, error }` entry).
  const uploadOne = useCallback((file, context) => {
    const storagePath = buildStoragePath(file, context)
    const storageRef = ref(storage, storagePath)
    const task = uploadBytesResumable(storageRef, file, { contentType: file.type })

    return new Promise((resolve) => {
      task.on(
        'state_changed',
        (snapshot) => {
          const percent = snapshot.totalBytes
            ? snapshot.bytesTransferred / snapshot.totalBytes
            : 0
          setProgress((prev) => ({ ...prev, [file.name]: percent }))
        },
        (err) => {
          setError((prev) => [
            ...prev.filter((entry) => entry.fileName !== file.name),
            { fileName: file.name, error: err }
          ])
          resolve(null)
        },
        async () => {
          setProgress((prev) => ({ ...prev, [file.name]: 1 }))
          setError((prev) => prev.filter((entry) => entry.fileName !== file.name))
          await getDownloadURL(task.snapshot.ref)
          resolve(
            toAttachmentRef({
              storagePath,
              fileName: file.name,
              contentType: file.type,
              size: file.size
            })
          )
        }
      )
    })
  }, [buildStoragePath])

  // Upload each file, returning the array of successful attachment refs. Files
  // that fail are recorded in `error` and can be retried individually.
  const upload = useCallback(async (files, context = {}) => {
    const fileList = Array.from(files || [])
    if (fileList.length === 0) return []

    setUploading(true)

    fileList.forEach((file) => {
      pendingRef.current[file.name] = { file, context }
    })

    const results = await Promise.all(
      fileList.map((file) => uploadOne(file, context))
    )

    setUploading(false)

    return results.filter((refItem) => refItem !== null)
  }, [uploadOne])

  // Re-attempt the upload for a previously-failed file, clearing its error on
  // success (Req 2.4).
  const retry = useCallback(async (fileName) => {
    const pending = pendingRef.current[fileName]
    if (!pending) return null

    setUploading(true)
    const result = await uploadOne(pending.file, pending.context)
    setUploading(false)

    return result
  }, [uploadOne])

  return { upload, uploading, progress, error, retry }
}

export default useAttachmentUpload
